import React, { useState, useEffect } from 'react';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';

// ---------------------------------------------------------------------------
// Loading skeleton rendered inside a card while parent data is fetching
// ---------------------------------------------------------------------------
const CardLoading = () => (
  <div className="space-y-4">
    {[1, 2, 3].map(i => (
      <div key={i} className="animate-pulse">
        <div className="border border-gray-200 rounded-2xl p-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded-xl w-3/4" />
              <div className="h-3 bg-gray-200 rounded-xl w-1/2" />
            </div>
            <div className="flex space-x-2">
              <div className="w-8 h-8 bg-gray-200 rounded-xl" />
              <div className="w-8 h-8 bg-gray-200 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Empty state with contextual messaging per category
// ---------------------------------------------------------------------------
const EmptyState = ({ category, title, searchTerm, onClearSearch, onAddNew, readOnly }) => {
  const emptyStateMap = {
    departments: {
      icon: '🏢',
      title: 'No departments configured',
      description: 'Departments are the foundation of your organisational structure. Start by adding your main business units.',
      actionText: 'Add First Department',
      tips: ['Consider your organisational chart', 'Start with major divisions', 'Add smaller teams later'],
    },
    locations: {
      icon: '📍',
      title: 'No locations set up',
      description: 'Locations help track where your employees work and manage timezone differences.',
      actionText: 'Add First Location',
      tips: ['Include your main office', 'Add remote work locations', 'Set correct timezones'],
    },
    publicHolidays: {
      icon: '🎉',
      title: 'No public holidays configured',
      description: 'Public holidays are essential for accurate leave calculations and payroll processing.',
      actionText: 'Add Holiday',
      tips: ['Start with national holidays', 'Include religious observances', 'Mark gazetted holidays'],
    },
    leaveTypes: {
      icon: '🏖️',
      title: 'No leave types defined',
      description: 'Leave types define what kinds of time off employees can take and the rules that apply.',
      actionText: 'Add Leave Type',
      tips: ['Start with annual leave', 'Add sick leave', 'Include maternity/paternity leave'],
    },
  };

  const content = emptyStateMap[category] || {
    icon: '📋',
    title: `No ${title.replace(/[^\w\s]/g, '').trim().toLowerCase()} configured`,
    description: `Configure ${title.replace(/[^\w\s]/g, '').trim().toLowerCase()} to customise how your HR system operates.`,
    actionText: `Add ${title.replace(/[^\w\s]/g, '').trim()}`,
    tips: ['Check similar organisations', 'Start with the basics', 'Refine over time'],
  };

  if (searchTerm) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No results found</h3>
        <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
          No {title.replace(/[^\w\s]/g, '').trim().toLowerCase()} matched &ldquo;<span className="font-medium">{searchTerm}</span>&rdquo;.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
          <button onClick={onClearSearch} className="btn btn-admin-outline self-center sm:self-auto">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear Search
          </button>
          {!readOnly && (
            <button onClick={onAddNew} className="btn btn-admin-primary self-center sm:self-auto">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {content.actionText}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="text-center py-12">
      <div className="text-6xl mb-4">{content.icon}</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{content.title}</h3>
      <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">{content.description}</p>

      {readOnly ? (
        <p className="text-xs text-gray-400">This is reference data managed by the system.</p>
      ) : (
        <>
          <button onClick={onAddNew} className="btn btn-admin-primary">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {content.actionText}
          </button>

          {content.tips && (
            <div className="mt-8 bg-red-50 rounded-2xl p-4 max-w-md mx-auto text-left">
              <h4 className="text-sm font-medium text-red-900 mb-2">💡 Quick Tips</h4>
              <ul className="text-xs text-red-800 space-y-1">
                {content.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span aria-hidden="true">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-category badge colour
// ---------------------------------------------------------------------------
const getCategoryBadgeColor = (category) => {
  const colorMap = {
    departments: 'bg-blue-100 text-blue-800',
    locations: 'bg-green-100 text-green-800',
    classifications: 'bg-purple-100 text-purple-800',
    designations: 'bg-indigo-100 text-indigo-800',
    employmentTypes: 'bg-slate-100 text-slate-800',
    payGrades: 'bg-emerald-100 text-emerald-800',
    institutionTypes: 'bg-amber-100 text-amber-800',
    educationInstitutions: 'bg-yellow-100 text-yellow-800',
    qualificationLevels: 'bg-orange-100 text-orange-800',
    fieldsOfStudy: 'bg-teal-100 text-teal-800',
    certificationAuthorities: 'bg-cyan-100 text-cyan-800',
    certificationCategories: 'bg-sky-100 text-sky-800',
    certificationTemplates: 'bg-blue-100 text-blue-800',
    tradeCategories: 'bg-stone-100 text-stone-800',
    competencyLevels: 'bg-zinc-100 text-zinc-800',
    skillFrameworks: 'bg-neutral-100 text-neutral-800',
    safetyCertifications: 'bg-red-100 text-red-800',
    leaveTypes: 'bg-cyan-100 text-cyan-800',
    holidayCalendars: 'bg-purple-100 text-purple-800',
    publicHolidays: 'bg-pink-100 text-pink-800',
    momWorkingDaysCalendar: 'bg-indigo-100 text-indigo-800',
    salaryStructures: 'bg-green-100 text-green-800',
    cpfRates: 'bg-blue-100 text-blue-800',
    benefitPlans: 'bg-emerald-100 text-emerald-800',
    insurancePolicies: 'bg-teal-100 text-teal-800',
    workweekPolicies: 'bg-orange-100 text-orange-800',
    workScheduleTemplates: 'bg-blue-100 text-blue-800',
    overtimePolicies: 'bg-red-100 text-red-800',
    noticePolicies: 'bg-yellow-100 text-yellow-800',
    retirementPolicies: 'bg-gray-100 text-gray-800',
    competencyFrameworks: 'bg-stone-100 text-stone-800',
    cpdRequirements: 'bg-amber-100 text-amber-800',
    foreignWorkerLevies: 'bg-violet-100 text-violet-800',
    wicaCompensations: 'bg-rose-100 text-rose-800',
    workPassQuotas: 'bg-fuchsia-100 text-fuchsia-800',
    medicalExams: 'bg-pink-100 text-pink-800',
    systemSettings: 'bg-gray-100 text-gray-800',
    integrationKeys: 'bg-slate-100 text-slate-800',
    customFields: 'bg-zinc-100 text-zinc-800',
    auditConfigs: 'bg-neutral-100 text-neutral-800',
    approvalRules: 'bg-green-100 text-green-800',
    notificationTemplates: 'bg-blue-100 text-blue-800',
  };
  return colorMap[category] || 'bg-gray-100 text-gray-800';
};

// ---------------------------------------------------------------------------
// Main ConfigCard component
//
// Props:
//   title            - Card heading
//   icon             - Lucide React icon component (optional)
//   items            - Array of data records
//   category         - camelCase key matching configData key (used for endpoint mapping)
//   fields           - Additional field names to render below the item name
//   searchTerm       - Global search term from parent (string)
//   setSearchTerm    - Setter to clear the global search
//   globalCollapsed  - Object mapping category → boolean (parent-managed collapse state)
//   setGlobalCollapsed - Setter to sync collapse into parent state
//   onAddNew(category)       - Called when "Add New" is clicked
//   onEdit(category, item)   - Called when edit icon is clicked
//   onDelete(category, item) - Called when delete icon is clicked
//   readOnly         - When true: hides Add/Edit/Delete controls; shows "Reference" badge
// ---------------------------------------------------------------------------
const ConfigCard = ({
  title,
  icon: Icon,
  items,
  category,
  fields = [],
  searchTerm,
  setSearchTerm,
  globalCollapsed,
  setGlobalCollapsed,
  onAddNew,
  onEdit,
  onDelete,
  readOnly = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(globalCollapsed?.[category] || false);
  const [currentPage, setCurrentPage] = useState(1);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const itemsPerPage = 5;

  // Reset to page 1 when search terms change
  useEffect(() => {
    setCurrentPage(1);
  }, [localSearchTerm, searchTerm]);

  // Sync collapse state driven by parent's "Collapse All" action
  useEffect(() => {
    if (globalCollapsed?.[category] !== undefined) {
      setIsCollapsed(globalCollapsed[category]);
    }
  }, [globalCollapsed, category]);

  const handleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    setGlobalCollapsed?.(prev => ({ ...prev, [category]: next }));
  };

  const activeSearchTerm = searchTerm || localSearchTerm;

  const filteredItems = (items || []).filter(item => {
    if (!activeSearchTerm) return true;
    const searchableText = [
      item.department_name,
      item.location_name,
      item.key,
      item.holiday_name,
      item.leave_type_name,
      item.name,
      ...fields.map(f => item[f]),
    ].filter(Boolean).join(' ').toLowerCase();
    return searchableText.includes(activeSearchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  // Only show local search when there's data and no global search active
  const showLocalSearch = !searchTerm && !isCollapsed && (items || []).length > 0 && !readOnly;

  const displayName = (item, idx) =>
    item.department_name ||
    item.location_name ||
    item.key ||
    item.holiday_name ||
    item.leave_type_name ||
    // synthetic composite label added by backend serializer
    item.display_name ||
    // category-specific identifier fields
    item.relief_name ||         // irasTaxReliefs
    item.injury_type ||         // wicaCompensations
    item.pass_type_display ||   // workPassQuotas
    item.fund_type_display ||   // shgContributionTiers
    item.allowance_type_name || // allowanceTypes
    item.component_name ||      // salaryStructures
    item.trade_category_name || // cpdRequirements, skillFrameworks
    item.policy_name ||         // sdlPolicies
    item.model_name ||          // auditConfigs, customFields
    item.pass_type ||           // foreignWorkerLevies
    item.name ||
    `${category} ${idx + 1}`;

  const renderFieldValue = (item, field) => {
    const val = item[field];
    if (val == null || val === '') return null;
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'string' && field.includes('time')) return val.substring(0, 5);
    return val;
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      {/* ── Header ── */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={handleCollapse}
              className="icon-button-admin flex-shrink-0"
              aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
            >
              <svg
                className={`w-4 h-4 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {Icon && <Icon className="w-5 h-5 text-gray-500 flex-shrink-0" />}
            <h3 className="text-lg font-semibold text-gray-900 truncate">{title}</h3>

            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getCategoryBadgeColor(category)}`}>
              {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
            </span>

            {searchTerm && filteredItems.length !== (items || []).length && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex-shrink-0">
                {filteredItems.length} of {(items || []).length} shown
              </span>
            )}

            {readOnly && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 flex-shrink-0">
                Reference
              </span>
            )}
          </div>

          {!readOnly && (
            <button
              onClick={() => onAddNew?.(category)}
              className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-shrink-0 ml-3"
              title={`Add new ${title.replace(/[^\w\s]/g, '').trim().toLowerCase()}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="hidden sm:inline">Add New</span>
            </button>
          )}
        </div>

        {/* Local search (hidden when global search is active or card is read-only) */}
        {showLocalSearch && (
          <div className="mt-3">
            <DebouncedSearchInput
              value={localSearchTerm}
              onSearch={setLocalSearchTerm}
              placeholder={`Search ${title.replace(/[^\w\s]/g, '').trim().toLowerCase()}...`}
            />
          </div>
        )}
      </div>

      {/* ── Content ── */}
      {!isCollapsed && (
        <div className="px-6 py-4">
          {filteredItems.length > 0 ? (
            <>
              <div className="space-y-3">
                {displayItems.map((item, index) => (
                  <div
                    key={item.id ?? index}
                    className="group border border-gray-200 rounded-xl p-4 hover:border-gray-300 hover:bg-gray-50/50 transition-all"
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-gray-900 truncate">
                            {displayName(item, startIndex + index)}
                          </h4>
                          {item.head_of_department && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              HOD: {item.head_of_department_name || 'Assigned'}
                            </span>
                          )}
                        </div>

                        {fields.length > 0 && (
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {fields.map(field => {
                              const val = renderFieldValue(item, field);
                              return val != null ? (
                                <div key={field} className="text-sm text-gray-600">
                                  <span className="font-medium text-gray-700 capitalize">
                                    {field.replace(/_/g, ' ')}:
                                  </span>{' '}
                                  <span className="text-gray-900">{String(val)}</span>
                                </div>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>

                      {!readOnly && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button
                            onClick={() => onEdit?.(category, item)}
                            className="icon-button-admin"
                            title="Edit"
                            aria-label="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => onDelete?.(category, item)}
                            className="icon-button-admin"
                            title="Delete"
                            aria-label="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredItems.length)} of {filteredItems.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className="btn btn-admin-outline btn-sm"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="btn btn-admin-outline btn-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <EmptyState
              category={category}
              title={title}
              searchTerm={activeSearchTerm}
              onClearSearch={() => (searchTerm ? setSearchTerm?.('') : setLocalSearchTerm(''))}
              onAddNew={() => onAddNew?.(category)}
              readOnly={readOnly}
            />
          )}
        </div>
      )}
    </div>
  );
};

export { CardLoading };
export default ConfigCard;
